import { Box, Button, Card, Container, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";
import CollapsibleTestModuleTable from "src/views/tables/CollapsibleTestModuleTable";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import FallbackSpinner from "src/@core/components/spinner";
import useFinancePermissions from "src/hooks/useFinancePermissions";

const MyCard = styled(Card)({
  width: "100%",
  background: "#ffffff",
  border: "1px solid #edf4ff",
  boxShadow: "4px 4px 30px rgba(22, 24, 26, 0.05)",
  borderRadius: "15px",
  padding: "1.5rem",
  height: "90vh",
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

const ScrollableTableBox = styled(Box)({
  flex: 1,
  overflowY: "auto",
  width: "100%",
  marginTop: "1rem",
  paddingBottom: "4rem",
});

const ButtonStyle = styled(Button)({
  backgroundColor: "#6200EE",
  textTransform: "lowercase",
  color: "white",
  fontWeight: 700,
  fontSize: "17px",
  borderRadius: "14px",
  maxWidth: "155px",
  minWidth: "100px",
  "&:hover": {
    backgroundColor: "#6200EE",
    color: "white",
  }
});

const TypoStyle = styled('Typography')((props) => ({
  color: `${props.color} !important`,
  fontWeight: `${props.fontWeight} !important`,
  fontSize: `${props.fontSize} !important`,
  ...props,
}));

const schema = yup.object().shape({
  locationCount: yup
    .number()
    .required("location count is required")
    .min(1, "Minimum 1 location"),

  headlineCharacterCount: yup
    .number()
    .required("headline character count is required")
    .min(1, "min 1 characters"),

  bodyCharacterCount: yup
    .number()
    .required("body character count is required")
    .min(1, "max 1 characters"),

  duration: yup
    .number()
    .required("duration is required")
    .min(1, "minimum duration is 1 hour"),

  proximity: yup
    .number()
    .required("proximity is required")
    .min(0.1, "must be positive"),

  adType: yup
    .string()
    .required("ad type is required"),

  countryCode: yup
    .string()
    .required("country code is required"),

  region: yup
    .string()
    .required("region is required"),
});

const useAdPriceTestingForm = (defaultValues = {}) => {
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      locationCount: 6,
      headlineCharacterCount: 80,
      bodyCharacterCount: 250,
      duration: 1,
      proximity: 0.28,
      adType: 'instant_message',
      countryCode: "US",
      region: "California",
      image: 1,
      video: 1,
      ...defaultValues,
    },
    resolver: yupResolver(schema),
  });

  return {
    control,
    handleSubmit,
    register,
    errors,
    reset,
    watch,
    setValue,
  };
};


function AdSettingsTestModule() {

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { canEdit } = useFinancePermissions();
  const canEditAdSettings = canEdit('adSettings');

  const [amount, setAmount] = useState(0);

  const handleNavigate = () => {
    navigate("/ad-settings");
  }

  const { mutate, isLoading, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (data) => {
      const response = await axiosInstance.post(ApiEndPoints.ADD_SETTINGS.adsSettingsTest, data);
      return response.data;
    },
    onSuccess: (data) => {
      console.log('data:baseCost ', data);
      const baseCost = data?.data?.breakdown?.total;
      setAmount(baseCost);
      enqueueSnackbar(data?.message, {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    },
    onError: (err) => {
      enqueueSnackbar(err?.response?.data?.message, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
      console.error("Failed to update profile:", err.message);
    },
  });

  const { data: adSettingsTest, refetch: refetchAdSettingsTest, isLoading: testAdSettingsLoader } = useQuery({
    queryKey: ["getAdSettingsTest"],
    queryFn: async () => {
      const response = await axiosInstance.get(ApiEndPoints.ADD_SETTINGS.adsSettingsGetTest);
      const res = response?.data?.data;
      console.log('data:adSettingsTest ', res);
      return res;
    },
    refetchOnWindowFocus: false,
  });
  console.log('adSettingsTest: ', adSettingsTest);
  if (testAdSettingsLoader) {
    return (
      <FallbackSpinner />
    )
  }
  return (
    <Container maxWidth="xl">
      <MyCard elevation={10}>
        <TypoStyle
          fontWeight={400}
          fontSize={"16px"}
          textTransform={"lowercase"}
          textAlign={"left"}
        >
          test out a set of values for a fast marketing campaign
        </TypoStyle>
        <ScrollableTableBox>
          <CollapsibleTestModuleTable adSettingsTest={adSettingsTest?.data} useAdPriceTestingForm={useAdPriceTestingForm} mutate={mutate} amount={amount} isLoading={isPending} canEdit={canEditAdSettings} />
        </ScrollableTableBox>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            paddingX: "20px"
          }}
        >
          <TypoStyle
            fontWeight={700}
            fontSize={"15px"}
            textTransform={"lowercase"}
            color={"#000000"}
          >
            these costs exclude country-based taxes
          </TypoStyle>
          <ButtonStyle variant="contained" onClick={handleNavigate}>back to main</ButtonStyle>
        </Box>
      </MyCard>
    </Container>
  );
}

export default AdSettingsTestModule;
